import React, { useContext, useState } from 'react'
import OrganizationSeatPricingAndTerms from './OrganizationSeatPricingAndTerms'
import { AuthContext } from '@/context/auth-context'
import { WorkEmailInput } from '@/components/work-email-input/WorkEmailInput'
import { getBlockedFreeEmailValidationMessage } from '@/components/work-email-input/work-email-policy'
import { getOrganizationInviteEmailError } from '@/organizations/utils/organization-invite-email'

type OrganizationSetupProps = {
  orgName: string
  setOrgName: (orgName: string) => void
  emails: string[]
  setEmails: (emails: string[]) => void
  emailValue: string
  setEmailValue: (emailValue: string) => void
}

const OrganizationSetup: React.FC<OrganizationSetupProps> = ({ orgName, setOrgName, emails, setEmails, emailValue, setEmailValue }) => {
  // Auth context
  const { userAccountData, user } = useContext(AuthContext)
  const isSubscriptionExempt = userAccountData?.isSubscriptionExempt

  // Local state
  const [emailError, setEmailError] = useState<string | null>(null)
  const hasInviteEmails = emails.length > 0

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailValue(e.target.value)
    setEmailError(null) // Clear error when user types
  }

  const validateAndAddEmail = (email: string) => {
    const trimmedEmail = email.trim()
    const validationError = getOrganizationInviteEmailError(trimmedEmail, emails, user?.email)
    if (validationError) {
      setEmailError(validationError)
      return false
    }

    setEmails([...emails, trimmedEmail])
    setEmailValue('')
    setEmailError(null)
    return true
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === ' ' || e.key === 'Enter') && emailValue) {
      e.preventDefault() // Prevent space from being added to the input
      validateAndAddEmail(emailValue)
    } else {
      setEmailError(null) // Clear error for other key presses
    }
  }

  const handleAddEmail = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    validateAndAddEmail(emailValue)
  }

  const removeEmail = (emailToRemove: string) => {
    setEmails(emails.filter((email) => email !== emailToRemove))
  }

  return (
    <form>
      <div className="py-6 md:py-8 space-y-6 md:space-y-8 border-b border-brand-neutral-900/10">
        {/* Organization Name */}
        <div>
          <label htmlFor="organization-name" className="font-medium">
            Organization Name
          </label>
          <p className="mt-1.5 text-sm leading-6 text-brand-neutral-600">This will appear in the account menu for all users of your organization.</p>
          <input
            id="organization-name"
            name="organization-name"
            type="text"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            placeholder=""
            autoComplete="organization"
            className="block w-full rounded-md border-0 mt-2 py-1.5 max-w-md ring-1 ring-inset ring-brand-neutral-400 placeholder:text-brand-neutral-500 focus:ring-inset focus:outline-hidden focus:ring-brand-400 sm:text-sm sm:leading-6"
          />
        </div>

        {/* Invite Team Members */}
        <div>
          <p className="font-medium">Invite Team Members</p>
          <p className="mt-1.5 text-sm text-brand-neutral-600">Invited team members will get an email to join. You can add more later.</p>
          <div className="max-w-lg">
            <div className="flex items-start gap-2 mt-2">
              <div className="flex-1 min-w-0">
                <WorkEmailInput
                  id="invite-emails"
                  name="invite-emails"
                  value={emailValue}
                  onChange={handleInputChange}
                  onKeyDown={handleInputKeyDown}
                  error={emailError ?? undefined}
                  getPolicyError={getBlockedFreeEmailValidationMessage}
                  className="!pb-1 !pt-5"
                />
              </div>

              <button
                type="button"
                onClick={handleAddEmail}
                disabled={!!emailError || !emailValue.trim()}
                className="h-12 px-4 text-sm font-semibold rounded-md ring-1 ring-inset ring-brand-500 text-brand-500 enabled:hocus:bg-brand-neutral-100 focus:outline-hidden disabled:cursor-not-allowed disabled:ring-true-gray-300 disabled:text-true-gray-400"
              >
                Add
              </button>
            </div>

            {/* Invite Emails Chips */}
            {hasInviteEmails && (
              <div className="mt-3 flex flex-wrap gap-2" role="list">
                {emails.map((email) => (
                  <span
                    key={email}
                    role="listitem"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-sm font-medium bg-brand-neutral-100 text-brand-neutral-600"
                  >
                    <span className="truncate max-w-[16rem]">{email}</span>
                    <button
                      type="button"
                      onClick={() => removeEmail(email)}
                      aria-label={`Remove ${email}`}
                      className="ml-0.5 w-5 h-5 flex items-center justify-center rounded-full border border-brand-neutral-300 text-brand-neutral-500 hover:bg-brand-neutral-200 hover:text-brand-neutral-700 focus:outline-hidden transition-colors"
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Pricing & Terms */}
        {!isSubscriptionExempt && <OrganizationSeatPricingAndTerms />}
      </div>
    </form>
  )
}

export default OrganizationSetup
